// utils/ssmlUtils.js

/**
 * Converts plain text to SSML with proper handling of initials
 * @param {string} text - The text to convert
 * @param {string} languageCode - Optional language code to apply language-specific rules
 * @returns {string} - Text wrapped in SSML tags with proper formatting
 */
export function convertToSSML(text, languageCode = 'en') {
    if (!text) return "<speak></speak>";

    // Work with the raw text first, then wrap in <speak> tags at the end
    let processed = text;

    // Apply common abbreviation replacements
    processed = applyCommonAbbreviations(processed);

    // Apply language-specific replacements
    processed = applyLanguageSpecificReplacements(processed, languageCode);

    // Apply initial handling (most specific to most general)
    processed = applyInitialHandling(processed);

    // Add break after periods
    processed = processed.replace(/\.(?=\s|$)/g, ".<break time=\"0.5s\"/>");
    // Add break between paragraphs
    processed = processed.replace(/\n\s*\n/g, "<break time=\"0.85s\"/>");

    // Finally, wrap in <speak> tags if not already wrapped
    // NOTE: Do NOT add voice tags here - let the backend handle voice selection
    if (!processed.startsWith('<speak>')) {
      processed = `<speak>${processed}</speak>`;
    }

    return processed;
  }
  
  /**
   * Replaces common abbreviations that should be expanded
   * @param {string} text - The text to process
   * @returns {string} - Text with abbreviations replaced
   */
  function applyCommonAbbreviations(text) {
    let processed = text;
    
    // Common abbreviations for all languages
    processed = processed.replace(/\bU\.S\.A\./g, "USA");
    processed = processed.replace(/\bU\.K\./g, "UK");
    processed = processed.replace(/\bU\.S\./g, "US");
    processed = processed.replace(/\bDr\./g, "Doctor");
    
    return processed;
  }
  
  /**
   * Applies language-specific replacements
   * @param {string} text - The text to process
   * @param {string} languageCode - Language code (e.g., 'en', 'pt', 'es')
   * @returns {string} - Text with language-specific replacements
   */
  function applyLanguageSpecificReplacements(text, languageCode) {
    let processed = text;
    
    // Get the base language code (e.g., 'en' from 'en-US')
    const baseLanguage = languageCode.split('-')[0];
    
    switch (baseLanguage) {
      case 'pt': // Portuguese
        processed = processed.replace(/\bSr\./g, "Senhor");
        processed = processed.replace(/\bSra\./g, "Senhora");
        processed = processed.replace(/\bAv\./g, "Avenida");
        break;
      
      case 'es': // Spanish
        processed = processed.replace(/\bSr\./g, "Señor");
        processed = processed.replace(/\bSra\./g, "Señora");
        processed = processed.replace(/\bAvda\./g, "Avenida");
        break;
        
      case 'fr': // French
        processed = processed.replace(/\bM\./g, "Monsieur");
        processed = processed.replace(/\bMme\./g, "Madame");
        processed = processed.replace(/\bBd\./g, "Boulevard");
        break;
        
      case 'de': // German
        processed = processed.replace(/\bHr\./g, "Herr");
        processed = processed.replace(/\bFr\./g, "Frau");
        processed = processed.replace(/\bStr\./g, "Strasse");
        break;
        
      default: // English and other languages
        // Handle titles before initials
        const titles = ["Prof", "Dr", "Mr", "Mrs", "Ms", "Rev", "St"];
        const titlePattern = new RegExp(`\\b(${titles.join("|")})\\.\\s+`, "g");
        processed = processed.replace(titlePattern, "$1 ");
        break;
    }
    
    return processed;
  }
  
  /**
   * Handles all types of initials in text for proper SSML pronunciation
   * @param {string} text - The text to process
   * @returns {string} - Text with initials properly formatted for SSML
   */
  function applyInitialHandling(text) {
    let processed = text;
    
    // HANDLE INITIALS IN THE RIGHT ORDER (most specific to most general)
    
    // 1. Triple initials followed by a word (e.g., "J.R.R. Tolkien")
    processed = processed.replace(/\b([A-Z])\.\s*([A-Z])\.\s*([A-Z])\.\s+(\w+)/g, 
      '<say-as interpret-as="characters">$1</say-as> <say-as interpret-as="characters">$2</say-as> <say-as interpret-as="characters">$3</say-as> $4');
    
    // 2. Triple initials not followed by a word (e.g., "J.R.R.")
    processed = processed.replace(/\b([A-Z])\.\s*([A-Z])\.\s*([A-Z])\./g, 
      '<say-as interpret-as="characters">$1</say-as> <say-as interpret-as="characters">$2</say-as> <say-as interpret-as="characters">$3</say-as>');
    
    // 3. Double initials followed by a word (e.g., "D.W. Eisenhower")
    processed = processed.replace(/\b([A-Z])\.\s*([A-Z])\.\s+(\w+)/g,
      '<say-as interpret-as="characters">$1</say-as> <say-as interpret-as="characters">$2</say-as> $3');
    
    // 4. Double initials not followed by a word (e.g., "J.K.")
    processed = processed.replace(/\b([A-Z])\.\s*([A-Z])\./g, 
      '<say-as interpret-as="characters">$1</say-as> <say-as interpret-as="characters">$2</say-as>');
    
    // 5. Single initial followed by a word (e.g., "D. Afonso") - CRITICAL for your example
    processed = processed.replace(/\b([A-Z])\.\s+(\w+)/g, 
      '<say-as interpret-as="characters">$1</say-as> $2');
    
    // 6. Standalone single initial (e.g., "The name is J.")
    processed = processed.replace(/\b([A-Z])\./g, 
      '<say-as interpret-as="characters">$1</say-as>');
    
    return processed;
  }
  
  /**
   * Extracts language code from a voice ID
   * @param {string} voiceId - Voice ID (e.g., "pt-BR-Standard-C")
   * @returns {string} - Language code (e.g., "pt-BR")
   */
  export function getLanguageFromVoiceId(voiceId) {
    if (!voiceId) return 'en';
    
    const parts = voiceId.split('-');
    if (parts.length >= 2) {
      return `${parts[0]}-${parts[1]}`;
    }
    return 'en';
  }